import { useQuery } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  GetAllEvacuationEventsParticipantsBody,
  GetAllEvacuationEventsBody,
  GetMusterPointsDropdownByLocationBody,
  GetAllMusterPointsByEvacuationIdBody,
  GetEvacuationEventsParticipantsCountsBody,
  GetEvacuationEventByIdBody,
  GetEvacuationEventReportBody,
  GetUserDetailsByEvacuationIdBody,
  GetAllEvacuationEventsCodesBody,
} from "../interface";

export const FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS = {
  FETCH_MUSTER_POINTS_BY_LOCATION: "FETCH_MUSTER_POINTS_BY_LOCATION",
  FETCH_ALL_EVACUATION_EVENTS: "FETCH_ALL_EVACUATION_EVENTS",
  FETCH_ALL_EVACUATION_EVENT_PARTICIPANTS:
    "FETCH_ALL_EVACUATION_EVENT_PARTICIPANTS",
  FETCH_ALL_MUSTER_POINTS_BY_EVACUATION_ID:
    "FETCH_ALL_MUSTER_POINTS_BY_EVACUATION_ID",
  FETCH_EVACUATION_EVENT_PARTICIPANTS_COUNTS:
    "FETCH_EVACUATION_EVENT_PARTICIPANTS_COUNTS",
  FETCH_ALL_EVACUATION_EVENTS_CODES: "FETCH_ALL_EVACUATION_EVENTS_CODES",
  FETCH_EVACUATION_EVENT_BY_ID: "FETCH_EVACUATION_EVENT_BY_ID",
  FETCH_MUSTER_POINTS_BY_USER: "FETCH_MUSTER_POINTS_BY_USER",
  FETCH_EVACUATION_EVENT_REPORT: "FETCH_EVACUATION_EVENT_REPORT",
  FETCH_USER_DETAILS_BY_EVACUATION_ID: "FETCH_USER_DETAILS_BY_EVACUATION_ID",
};

const getEvacuationEventReportAction = async (
  body: GetEvacuationEventReportBody,
) => {
  return Post({
    url: "/api/Reports/GetEvacuationEventReport",
    body,
  });
};

export const useGetEvacuationEventReport = (
  body: GetEvacuationEventReportBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_EVACUATION_EVENT_REPORT,
      body,
    ],
    queryFn: () => getEvacuationEventReportAction(body),
  });
};

const getAllMusterPointsByEvacuationIdAction = async (
  body: GetAllMusterPointsByEvacuationIdBody,
) => {
  return Post({
    url: "/api/Evacuation/GetAllDropdownByEvacuation",
    body,
  });
};

export const useGetAllMusterPointsByEvacuationId = (
  body: GetAllMusterPointsByEvacuationIdBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_ALL_MUSTER_POINTS_BY_EVACUATION_ID,
      body,
    ],
    queryFn: () => getAllMusterPointsByEvacuationIdAction(body),
    enabled: !!body.n_EvacuationID,
  });
};

const getAllEvacuationEventsParticipantsAction = async (
  body: GetAllEvacuationEventsParticipantsBody,
) => {
  return Post({
    url: "/api/EvacuationEvent/GetAll",
    body,
  });
};

export const useGetAllEvacuationEventsParticipants = (
  body: GetAllEvacuationEventsParticipantsBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_ALL_EVACUATION_EVENT_PARTICIPANTS,
      body,
    ],
    queryFn: () => getAllEvacuationEventsParticipantsAction(body),
  });
};

const getEvacuationEventsParticipantsCountsAction = async (
  body: GetEvacuationEventsParticipantsCountsBody,
) => {
  return Post({
    url: "/api/EvacuationEvent/GetCounts",
    body,
  });
};

export const useGetEvacuationEventsParticipantsCounts = (
  body: GetEvacuationEventsParticipantsCountsBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_EVACUATION_EVENT_PARTICIPANTS_COUNTS,
      body,
    ],
    queryFn: () => getEvacuationEventsParticipantsCountsAction(body),
  });
};

const getMusterPointsDropdownByLocationAction = async (
  body: GetMusterPointsDropdownByLocationBody,
) => {
  return Post({
    url: "/api/MusterPoints/GetAllDropdownByLocation",
    body,
  });
};

export const useGetMusterPointsDropdownByLocation = (
  body: GetMusterPointsDropdownByLocationBody,
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_MUSTER_POINTS_BY_LOCATION,
      body,
    ],
    queryFn: () => getMusterPointsDropdownByLocationAction(body),
    enabled: !!body.n_LocationID && enabled,
  });
};

const getAllEvacuationEventsAction = async (
  body: GetAllEvacuationEventsBody,
) => {
  return Post({
    url: "/api/Evacuation/GetAll",
    body,
  });
};

export const useGetAllEvacuationEvents = (body: GetAllEvacuationEventsBody) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_ALL_EVACUATION_EVENTS,
      body,
    ],
    queryFn: () => getAllEvacuationEventsAction(body),
  });
};

const getAllEvacuationEventsCodesAction = async (
  body?: GetAllEvacuationEventsCodesBody,
) => {
  return Post({
    url: "/api/Evacuation/GetAllDropdown",
    body,
  });
};

export const useGetAllEvacuationEventsCodes = (
  body?: GetAllEvacuationEventsCodesBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_ALL_EVACUATION_EVENTS_CODES,
    ],
    queryFn: () => getAllEvacuationEventsCodesAction(body),
  });
};

const getEvacuationEventByIdAction = async (
  body: GetEvacuationEventByIdBody,
) => {
  return Post({
    url: "/api/Evacuation/GetById",
    body,
  });
};

export const useGetEvacuationEventById = (body: GetEvacuationEventByIdBody) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_EVACUATION_EVENT_BY_ID,
    ],
    queryFn: () => getEvacuationEventByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getMusterPointsByUserAction = async () => {
  return Post({
    url: "/api/MusterPoints/GetAllDropdownByUser",
  });
};

export const useMusterPointsByUser = (enabled = true) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_MUSTER_POINTS_BY_USER,
    ],
    queryFn: () => getMusterPointsByUserAction(),
    enabled: enabled,
  });
};

const getUserDetailsByEvacuationIdAction = async (
  body: GetUserDetailsByEvacuationIdBody,
) => {
  return Post({
    url: "/api/EvacuationEvent/GetPersonDetailsById",
    body,
  });
};

export const useGetUserDetailsByEvacuationId = (
  body: GetUserDetailsByEvacuationIdBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_EMERGENCY_EVACUATION_DETAILS_KEYS.FETCH_USER_DETAILS_BY_EVACUATION_ID,
      body,
    ],
    queryFn: () => getUserDetailsByEvacuationIdAction(body),
    enabled: !!body.encryptedId,
    retry: false,
  });
};
